/**
 * Intelligent default values for business profile forms
 * These provide sensible starting points that users can customize
 */

export const getBusinessProfileDefaults = () => ({
    // Core business info - user must provide
    entity_name: '',
    contact_email: '',
    business_description: '',
    services_products_description: '',

    // Contact details with helpful placeholders
    contact_phone: '',
    contact_address: '',

    // Branding & Style - sensible defaults
    website_style_preference: 'Modern, clean, and professional with a focus on user experience and clear messaging',
    key_value_propositions: [
        'High-quality service and expertise',
        'Customer-focused approach',
        'Reliable and trustworthy solutions'
    ],
    brand_attributes: [
        'Professional',
        'Trustworthy',
        'Innovative',
        'Customer-focused'
    ],
    primary_call_to_action: 'Get Started',
    secondary_call_to_actions: [
        'Learn More',
        'Contact Us',
        'View Portfolio'
    ],

    // Content preferences - optimal defaults
    text_voice: 'Third Person (They/The Company)',
    text_formality: 'Professional',
    text_emotion_keywords: [
        'professional',
        'reliable',
        'innovative'
    ],

    // SEO - will be industry-specific
    seo_primary_keywords: [],
    seo_secondary_keywords: [],

    // Image generation - optimal defaults
    image_generation_required: true,
    image_art_style: 'photorealistic',
    image_composition_preference: 'Professional business environment with clean, modern aesthetics and good lighting',
    image_dominant_colors: ['#2563EB', '#1E40AF', '#FFFFFF', '#F8F9FA'], // Professional blue theme
    negative_image_prompts: [
        'outdated technology',
        'unprofessional settings',
        'cluttered spaces',
        'poor lighting'
    ],

    // Content generation preferences
    page_generation_mode: 'standard',
    blog_generation_mode: 'standard',
    requested_pages: [],
    requested_blog_topics: [],
    homepage_section_order_preference: [
        'Hero',
        'About',
        'Services',
        'Testimonials',
        'Contact'
    ],

    // Advanced settings
    target_language: 'en',
    preferred_content_length: 'medium',
    content_update_frequency: 'monthly',

    // Placeholder settings
    use_default_placeholder_images: false,

    // Social media placeholders
    social_media_links: [],
    logo_url: '',

    // Content to exclude - common exclusions
    content_to_exclude: [
        'specific pricing details',
        'competitor mentions',
        'outdated information'
    ]
});

/**
 * Industry-specific defaults that override base defaults
 */
export const getIndustryDefaults = (industryType) => {
    const baseDefaults = getBusinessProfileDefaults();

    switch (industryType?.toLowerCase()) {
        case 'technology':
            return {
                ...baseDefaults,
                website_style_preference: 'Sleek, modern, and tech-forward with clean lines and innovative design elements',
                brand_attributes: ['Innovative', 'Cutting-edge', 'Reliable', 'Scalable'],
                primary_call_to_action: 'Schedule Demo',
                secondary_call_to_actions: ['View Features', 'Start Free Trial', 'Contact Sales'],
                seo_primary_keywords: ['technology solutions', 'software development', 'digital transformation'],
                seo_secondary_keywords: ['tech consulting', 'innovation', 'digital solutions'],
                text_emotion_keywords: ['innovative', 'cutting-edge', 'advanced', 'efficient'],
                image_composition_preference: 'Modern tech office with diverse team, clean workspace, latest technology, professional lighting'
            };

        case 'healthcare':
            return {
                ...baseDefaults,
                website_style_preference: 'Clean, trustworthy, and accessible design that conveys professionalism and care',
                brand_attributes: ['Trustworthy', 'Caring', 'Professional', 'Reliable'],
                primary_call_to_action: 'Schedule Consultation',
                secondary_call_to_actions: ['Learn More', 'Find Location', 'Patient Portal'],
                seo_primary_keywords: ['healthcare services', 'medical care', 'health solutions'],
                seo_secondary_keywords: ['patient care', 'medical expertise', 'health professionals'],
                text_emotion_keywords: ['caring', 'professional', 'trustworthy', 'reliable'],
                image_composition_preference: 'Clean healthcare environment with diverse medical professionals, modern equipment, welcoming atmosphere',
                content_to_exclude: [...baseDefaults.content_to_exclude, 'medical advice', 'unverified health claims']
            };

        case 'finance':
            return {
                ...baseDefaults,
                website_style_preference: 'Professional, trustworthy, and sophisticated design that conveys stability and expertise',
                brand_attributes: ['Trustworthy', 'Professional', 'Secure', 'Expert'],
                primary_call_to_action: 'Schedule Consultation',
                secondary_call_to_actions: ['Learn More', 'View Services', 'Get Quote'],
                seo_primary_keywords: ['financial services', 'investment advice', 'financial planning'],
                seo_secondary_keywords: ['wealth management', 'financial consulting', 'investment solutions'],
                text_emotion_keywords: ['secure', 'professional', 'trustworthy', 'reliable'],
                image_composition_preference: 'Professional business environment, diverse professionals in business attire, modern office setting'
            };

        case 'retail':
            return {
                ...baseDefaults,
                website_style_preference: 'Vibrant, engaging, and user-friendly design that showcases products effectively',
                brand_attributes: ['Quality', 'Customer-focused', 'Trendy', 'Reliable'],
                primary_call_to_action: 'Shop Now',
                secondary_call_to_actions: ['View Catalog', 'Special Offers', 'Contact Us'],
                seo_primary_keywords: ['retail products', 'online shopping', 'quality products'],
                seo_secondary_keywords: ['customer service', 'product quality', 'shopping experience'],
                text_emotion_keywords: ['quality', 'stylish', 'affordable', 'trendy'],
                image_composition_preference: 'Clean product displays, happy customers, attractive store environment, good lighting'
            };

        case 'consulting':
            return {
                ...baseDefaults,
                website_style_preference: 'Professional, authoritative, and results-focused design that demonstrates expertise',
                brand_attributes: ['Expert', 'Results-driven', 'Strategic', 'Trusted'],
                primary_call_to_action: 'Schedule Consultation',
                secondary_call_to_actions: ['View Case Studies', 'Our Expertise', 'Contact Us'],
                seo_primary_keywords: ['business consulting', 'strategic advice', 'consulting services'],
                seo_secondary_keywords: ['business strategy', 'expert consulting', 'professional advice'],
                text_emotion_keywords: ['expert', 'strategic', 'results-driven', 'professional'],
                image_composition_preference: 'Professional business meetings, strategic planning sessions, diverse team collaboration'
            };

        default:
            return baseDefaults;
    }
};

/**
 * Get target audience suggestions based on industry
 */
export const getTargetAudienceSuggestions = (industryType) => {
    switch (industryType?.toLowerCase()) {
        case 'technology':
            return 'Businesses seeking digital transformation, IT decision-makers, and organizations looking to modernize their technology infrastructure';
        case 'healthcare':
            return 'Patients seeking quality healthcare services, families looking for trusted medical care, and individuals prioritizing their health and wellness';
        case 'finance':
            return 'Individuals and businesses seeking financial guidance, investors looking for expert advice, and clients who value financial security and growth';
        case 'retail':
            return 'Consumers looking for quality products, style-conscious shoppers, and customers who value excellent service and competitive prices';
        case 'consulting':
            return 'Business leaders seeking strategic guidance, organizations facing operational challenges, and companies looking to optimize performance and growth';
        default:
            return 'Customers who value quality service, professionalism, and reliable solutions tailored to their specific needs';
    }
};

/**
 * Generate SEO keywords based on business description
 */
export const generateSEOKeywords = (businessDescription, industryType) => {
    const commonKeywords = ['professional services', 'expert solutions', 'quality service'];
    const industryKeywords = {
        technology: ['software development', 'digital solutions', 'tech consulting', 'innovation'],
        healthcare: ['medical services', 'patient care', 'health solutions', 'medical expertise'],
        finance: ['financial planning', 'investment advice', 'wealth management', 'financial consulting'],
        retail: ['quality products', 'customer service', 'retail solutions', 'shopping experience'],
        consulting: ['business consulting', 'strategic planning', 'expert advice', 'business solutions']
    };

    const industry = industryType?.toLowerCase();
    const baseKeywords = industryKeywords[industry] || commonKeywords;

    // Extract key terms from business description
    const description = businessDescription.toLowerCase();
    const extractedTerms = [];

    // Common business terms that make good keywords
    const businessTerms = [
        'consulting', 'services', 'solutions', 'development', 'management', 'strategy',
        'design', 'marketing', 'technology', 'software', 'digital', 'online',
        'professional', 'expert', 'custom', 'innovative', 'quality'
    ];

    businessTerms.forEach(term => {
        if (description.includes(term) && !extractedTerms.includes(term)) {
            extractedTerms.push(term);
        }
    });

    return [...baseKeywords, ...extractedTerms.slice(0, 3)];
}; 